import { useEffect, useState } from "react";

import { Mic, MicOff, Pause, Play, MessageSquare, PhoneOff, Phone, AlertTriangle } from "lucide-react";

import { cn } from "../../lib/utils";
import { nivelLocal, nivelRemoto, modoDoAudio } from "../../ligacoes/store";
import { Avatar, BotaoRedondo, Cronometro, Ondas, formatarNumero, nomeDe } from "./pecas";

// Anel que respira com a voz do outro lado: sem precisar de "alô?" para
// saber se o áudio está chegando. Degraus de escala em classes do Tailwind.
const DEGRAUS = [
	"scale-100 opacity-20",
	"scale-105 opacity-40",
	"scale-110 opacity-60",
	"scale-115 opacity-75",
	"scale-125 opacity-90",
];

const AvatarComVoz = ({ ligacao, ativo }) => {
	const [degrau, setDegrau] = useState(0);
	useEffect(() => {
		if (!ativo) return undefined;
		let id;
		let suave = 0;
		const loop = () => {
			suave = suave * 0.75 + nivelRemoto() * 0.25;
			setDegrau(Math.min(4, Math.floor(suave * 20)));
			id = requestAnimationFrame(loop);
		};
		loop();
		return () => cancelAnimationFrame(id);
	}, [ativo]);

	return (
		<div className="relative size-24">
			{ativo && (
				<span
					className={cn(
						"absolute inset-0 rounded-full bg-emerald-500/50 transition-all duration-100",
						DEGRAUS[degrau]
					)}
				/>
			)}
			<Avatar
				nome={nomeDe(ligacao)}
				foto={ligacao?.contact?.profilePicUrl}
				tamanho={96}
				className="relative ring-4 ring-card"
			/>
		</div>
	);
};

// Cinco barrinhas que acendem com o seu microfone: prova de que ele capta.
const MeuMicrofone = ({ mudo }) => {
	// Guarda só quantas barras acendem (0–5): o React só redesenha quando muda.
	const [acesasAgora, setAcesas] = useState(0);
	useEffect(() => {
		let id;
		let suave = 0;
		const loop = () => {
			suave = suave * 0.7 + nivelLocal() * 0.3;
			setAcesas(Math.round(Math.min(1, suave * 4) * 5));
			id = requestAnimationFrame(loop);
		};
		loop();
		return () => cancelAnimationFrame(id);
	}, []);
	const acesas = mudo ? 0 : acesasAgora;
	return (
		<span className="flex items-end gap-[2px]" aria-hidden>
			{["h-1", "h-1.5", "h-2", "h-2.5", "h-3"].map((altura, i) => (
				<span
					key={altura}
					className={cn(
						"w-[3px] rounded-full transition-colors",
						altura,
						i < acesas ? "bg-emerald-500" : "bg-muted-foreground/25"
					)}
				/>
			))}
		</span>
	);
};

export const TelaRecebida = ({ ligacao, onAtender, onRecusar, ocupado }) => (
	<div className="flex flex-col items-center px-5 pb-6 pt-7 text-center">
		<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
			Ligação recebida
		</p>
		<div className="relative mt-6">
			<Ondas />
			<Avatar
				nome={nomeDe(ligacao)}
				foto={ligacao?.contact?.profilePicUrl}
				tamanho={88}
				className="relative ring-4 ring-card"
			/>
		</div>
		<p className="mt-5 max-w-full truncate text-xl font-semibold">{nomeDe(ligacao)}</p>
		{ligacao?.contact?.name && ligacao?.number && (
			<p className="text-sm tabular-nums text-muted-foreground">{formatarNumero(ligacao.number)}</p>
		)}
		<p className="mt-1 text-[11px] text-muted-foreground">
			pelo WhatsApp de <b className="font-medium text-foreground">{ligacao?.whatsapp?.name || "sua empresa"}</b>
		</p>
		<div className="mt-8 flex w-full justify-around">
			<BotaoRedondo
				icone={PhoneOff}
				rotulo="Recusar"
				perigo
				tamanho="lg"
				disabled={!!ocupado}
				carregando={ocupado === "recusar"}
				onClick={onRecusar}
			/>
			<BotaoRedondo
				icone={Phone}
				rotulo="Atender"
				sucesso
				tamanho="lg"
				disabled={!!ocupado}
				carregando={ocupado === "atender"}
				onClick={onAtender}
			/>
		</div>
	</div>
);

export const TelaEmLigacao = ({ ligacao, estado, onMudo, onEspera, onConversa, onDesligar, ocupado }) => {
	const conectada = ligacao.status === "connected" && !ligacao.endedAt;
	const [modo, setModo] = useState(null);
	useEffect(() => {
		const t = setInterval(() => setModo(modoDoAudio()), 1000);
		setModo(modoDoAudio());
		return () => clearInterval(t);
	}, []);

	let status;
	if (ligacao.endedAt) status = "Ligação encerrada";
	else if (estado.espera) status = "Em espera";
	else if (conectada) status = <Cronometro desde={ligacao.answeredAt} />;
	else if (ligacao.direction === "outbound") status = "Chamando";
	else status = "Conectando";

	return (
		<div className="flex flex-col items-center px-5 pb-6 pt-6 text-center">
			<div className="flex h-5 items-center gap-2">
				{modo === "opus" && (
					<span
						className="rounded-full border border-emerald-500/40 px-1.5 text-[10px] font-semibold tracking-wider text-emerald-600 dark:text-emerald-400"
						title="Áudio comprimido (Opus): ~24 kbps, qualidade de voz alta"
					>
						HD
					</span>
				)}
				<span className="text-[11px] text-muted-foreground">
					{ligacao.direction === "outbound" ? "Ligação feita" : "Ligação recebida"}
					{ligacao.whatsapp?.name ? ` · ${ligacao.whatsapp.name}` : ""}
				</span>
			</div>

			<div className="mt-6">
				<AvatarComVoz ligacao={ligacao} ativo={conectada && !estado.espera} />
			</div>

			<p className="mt-5 max-w-full truncate text-xl font-semibold">{nomeDe(ligacao)}</p>
			{ligacao.contact?.name && ligacao.number && (
				<p className="text-sm tabular-nums text-muted-foreground">{formatarNumero(ligacao.number)}</p>
			)}
			<p
				className={cn(
					"mt-2 flex h-6 items-center gap-2 text-sm",
					conectada && !estado.espera ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
				)}
			>
				{status}
				{!conectada && !ligacao.endedAt && (
					<span className="flex gap-0.5">
						{[0, 1, 2].map((i) => (
							<span
								key={i}
								className={cn(
									"h-1 w-1 animate-bounce rounded-full bg-current",
									["[animation-delay:0s]", "[animation-delay:150ms]", "[animation-delay:300ms]"][i]
								)}
							/>
						))}
					</span>
				)}
			</p>

			{estado.audio === "erro" && (
				<p className="mt-2 flex items-center gap-1.5 rounded-lg bg-red-500/10 px-2.5 py-1.5 text-xs text-red-600 dark:text-red-400">
					<AlertTriangle className="h-3.5 w-3.5 shrink-0" />
					{estado.erro || "Sem áudio: permita o microfone no navegador."}
				</p>
			)}

			<div className="mt-7 grid w-full grid-cols-3 gap-y-5">
				<BotaoRedondo
					icone={estado.mudo ? MicOff : Mic}
					rotulo={estado.mudo ? "Mudo" : "Microfone"}
					ativo={estado.mudo}
					onClick={onMudo}
					title={estado.mudo ? "Ligar microfone" : "Silenciar microfone"}
				/>
				<BotaoRedondo
					icone={estado.espera ? Play : Pause}
					rotulo={estado.espera ? "Retomar" : "Espera"}
					ativo={estado.espera}
					disabled={!conectada || !!ocupado}
					onClick={onEspera}
				/>
				<BotaoRedondo
					icone={MessageSquare}
					rotulo="Conversa"
					disabled={!ligacao.ticketId}
					onClick={onConversa}
					title="Abrir o atendimento deste contato"
				/>
			</div>

			<div className="mt-3 flex h-4 items-center gap-1.5 text-[10px] text-muted-foreground">
				<MeuMicrofone mudo={estado.mudo || estado.espera} />
				{estado.mudo ? "microfone desligado" : "seu microfone"}
			</div>

			<div className="mt-5">
				<BotaoRedondo
					icone={PhoneOff}
					perigo
					tamanho="lg"
					title="Desligar"
					disabled={!!ocupado}
					carregando={ocupado === "desligar"}
					onClick={onDesligar}
				/>
			</div>
		</div>
	);
};
